/**
 * Per-job lifecycle controller: executes the request pipeline and owns the
 * job's transport state. Port of fastSDK's `JobRuntime` + `JobTasks`,
 * collapsed to the Socaity/APIPod protocol (JS needs no task scheduler or
 * async bridge — one async function per job is the pipeline).
 *
 * Pipeline: prepare request → send → poll (when the response is a job
 * envelope) → process result. Invariants guarded here:
 * - at most one active StreamSession per job
 * - no stream open after a terminal state without a live source
 * - active streams close on cancel
 */

import { config } from '../core/config';
import { JobError } from '../core/errors';
import { ServiceAddress, resolveUrl } from '../schemas/definitions';
import {
  JobLinks,
  JobStatus,
  SocaityJobResponse,
  StreamingResponse,
  isJobResponse,
  jobStatusFromString,
} from '../schemas/transport';
import { StreamSession } from '../streaming/session';
import { assembleStreamBytes } from '../streaming/sse';
import { formatRequest } from './formatter';
import { HttpClient } from '../clients/http';
import { Job } from './job';
import { parseMediaResult, parseResponse } from './parser';

const MAX_CONSECUTIVE_POLL_ERRORS = 4;

export class JobRuntime<T = any> {
  private readonly abortController = new AbortController();
  private readonly pipeline: Promise<T | StreamingResponse>;

  private session: StreamSession | null = null;
  private openingStream = false;
  private directStream: StreamingResponse | null = null;
  private assembled: Promise<T> | null = null;
  private cancelled = false;
  /** Links are sticky: status responses may not echo them. */
  private links: JobLinks = {};
  private streamWaiters: (() => void)[] = [];

  constructor(
    private readonly job: Job<T>,
    private readonly address: ServiceAddress,
    private readonly http: HttpClient,
    private readonly params: Record<string, any>,
    /** When set, skip submission and resume polling from this envelope. */
    private readonly resumeFrom?: SocaityJobResponse,
  ) {
    this.pipeline = this.execute();
    // Users may consume via events or stream() only; avoid unhandled rejections.
    this.pipeline.catch(() => {});
  }

  // ------------------------------------------------------------------
  // Pipeline
  // ------------------------------------------------------------------

  private async execute(): Promise<T | StreamingResponse> {
    try {
      const parsed = this.resumeFrom ?? await this.submit();

      if (parsed instanceof StreamingResponse) {
        this.directStream = parsed;
        this.job._setStatus(JobStatus.STREAMING);
        this.notifyStreamState();
        return parsed; // sentinel: assembly happens in getResult() when never streamed
      }

      const result = isJobResponse(parsed)
        ? await this.pollUntilTerminal(parsed)
        : await parseMediaResult(parsed);

      this.job._finish(result);
      return result;
    } catch (error) {
      const failure = this.cancelled
        ? new JobError('Job was cancelled', this.job.id)
        : error instanceof Error ? error : new Error(String(error));
      this.job._fail(failure, this.cancelled ? JobStatus.CANCELLED : JobStatus.FAILED);
      this.notifyStreamState();
      throw failure;
    }
  }

  private async submit(): Promise<StreamingResponse | SocaityJobResponse | any> {
    const request = await formatRequest(this.address, this.job.endpoint, this.params);
    this.job._setStatus(JobStatus.PROCESSING);

    const response = await this.http.request(request.url, {
      method: request.method,
      query: request.query,
      body: request.body,
      headers: request.headers,
      signal: this.abortController.signal,
    });
    return parseResponse(response);
  }

  private async pollUntilTerminal(first: SocaityJobResponse): Promise<T> {
    const deadline = Date.now() + config.jobTimeoutMs;
    let envelope = first;
    let consecutiveErrors = 0;

    while (true) {
      this.mergeLinks(envelope);
      this.job._applyResponse({ ...envelope, links: { ...this.links } });
      this.notifyStreamState();

      const status = jobStatusFromString(envelope.status);
      if (status === JobStatus.FINISHED) {
        return parseMediaResult(envelope.result);
      }
      if (status === JobStatus.FAILED || status === JobStatus.TIMEOUT) {
        throw new JobError(envelope.error ?? `Job ended with status ${status}`, envelope.job_id);
      }
      if (status === JobStatus.CANCELLED) {
        this.cancelled = true;
        throw new JobError('Job was cancelled', envelope.job_id);
      }
      if (Date.now() > deadline) {
        throw new JobError(`Timed out after ${config.jobTimeoutMs} ms waiting for the job`, envelope.job_id);
      }

      await abortableSleep(config.pollIntervalMs, this.abortController.signal);

      const statusLink = this.links.status;
      if (!statusLink) {
        throw new JobError('Job response exposes no status link to poll', envelope.job_id);
      }

      try {
        const response = await this.http.request(resolveUrl(this.address, statusLink), {
          method: 'GET',
          signal: this.abortController.signal,
        });
        const parsed = await parseResponse(response);
        if (!isJobResponse(parsed)) {
          throw new JobError('Status endpoint returned an unexpected payload', envelope.job_id);
        }
        envelope = parsed;
        consecutiveErrors = 0;
      } catch (error) {
        consecutiveErrors += 1;
        if (this.abortController.signal.aborted || consecutiveErrors > MAX_CONSECUTIVE_POLL_ERRORS) {
          throw error;
        }
        // transient poll error: keep the last envelope and retry
      }
    }
  }

  private mergeLinks(envelope: SocaityJobResponse): void {
    this.links = {
      status: envelope.links?.status ?? this.links.status,
      cancel: envelope.links?.cancel ?? this.links.cancel,
      stream: envelope.links?.stream ?? this.links.stream,
    };
  }

  // ------------------------------------------------------------------
  // Results
  // ------------------------------------------------------------------

  /** Final result: pipeline output, or the assembled stream payload. */
  async getResult(): Promise<T> {
    const raw = await this.pipeline;
    if (!(raw instanceof StreamingResponse)) return raw;

    if (!this.assembled) this.assembled = this.assembleResult();
    const result = await this.assembled;
    this.job._finish(result);
    return result;
  }

  /** Drain the job's stream into one assembled result (text or media file). */
  private async assembleResult(): Promise<T> {
    const session = await this.stream();
    const data = await session.readAllBytes();
    return assembleStreamBytes(data, session.isSse) as Promise<T>;
  }

  // ------------------------------------------------------------------
  // Streaming
  // ------------------------------------------------------------------

  private hasStreamSource(): boolean {
    return this.directStream !== null || !!this.links.stream;
  }

  /** Open the job's live output stream and take the single session slot. */
  async stream(options: { timeoutMs?: number } = {}): Promise<StreamSession> {
    if (this.session || this.openingStream) {
      throw new Error(`Job ${this.job.id ?? ''} already has an active stream`);
    }
    if (this.job.isTerminal && !this.hasStreamSource()) {
      throw new Error(`Job ${this.job.id ?? ''} exposes no stream`);
    }

    this.openingStream = true;
    try {
      const deadline = Date.now() + (options.timeoutMs ?? 60_000);
      while (!this.hasStreamSource()) {
        if (this.job.isTerminal || this.cancelled) {
          throw new Error(`Job ${this.job.id ?? ''} exposes no stream`);
        }
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          throw new Error(`Job ${this.job.id ?? ''} exposed no stream within the timeout`);
        }
        await this.waitForStateChange(Math.min(remaining, 250));
      }

      let session: StreamSession;
      if (this.directStream) {
        session = new StreamSession(this.directStream.response);
      } else {
        const response = await this.http.request(resolveUrl(this.address, this.links.stream!), {
          method: 'GET',
          signal: this.abortController.signal,
        });
        session = new StreamSession(response);
      }
      this.session = session;
      return session;
    } finally {
      this.openingStream = false;
    }
  }

  private waitForStateChange(timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, timeoutMs);
      this.streamWaiters.push(done);
    });
  }

  private notifyStreamState(): void {
    this.streamWaiters.splice(0).forEach((wake) => wake());
  }

  // ------------------------------------------------------------------
  // Cancellation
  // ------------------------------------------------------------------

  /**
   * Cancel the job: close the active stream, request remote cancellation when
   * a cancel link exists, and abort local work. Resolves true when the remote
   * cancel was accepted (or none was needed).
   */
  async cancel(): Promise<boolean> {
    if (this.job.isTerminal) return false;
    this.cancelled = true;

    if (this.session) await this.session.close();

    let remoteAccepted = true;
    const cancelLink = this.links.cancel;
    if (cancelLink) {
      try {
        await this.http.request(resolveUrl(this.address, cancelLink), { method: 'POST' });
      } catch {
        remoteAccepted = false;
      }
    }

    this.abortController.abort(new JobError('Job was cancelled', this.job.id));
    this.notifyStreamState();
    return remoteAccepted;
  }
}

function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new Error('Aborted'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason ?? new Error('Aborted'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
